import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { fetchNonRoutableRelayFaxDetail } from 'store/thunks/workQueueThunks';

import { describeNonRoutableError } from '../utils/nonRoutableRequestErrors';

/**
 * POM-6279 — loads one parked relay fax (`nonRoutableRelayFaxDetail`) while its dialog is open.
 *
 * Read on every OPEN, never cached across opens: the view exists to decide between Resend,
 * Reconcile and Cancel, and a stale "send in progress" or fax code is exactly the wrong basis for
 * that. A response that lands after the dialog closed (or re-opened for another read) is dropped.
 *
 * Returns `{ loading, detail, described }`: `detail` is `null` when the id is not a parked relay
 * fax (an answer, not a failure); `described` is the typed error, described in place rather than
 * thrown at the global modal behind an open dialog.
 */
const useNonRoutableRelayFaxDetail = ({ nonRoutableRequestId, open }) => {
  const dispatch = useDispatch();
  const [state, setState] = useState({ loading: false, detail: undefined, described: undefined });

  useEffect(() => {
    if (!open) return undefined;

    let current = true;
    setState({ loading: true, detail: undefined, described: undefined });

    dispatch(fetchNonRoutableRelayFaxDetail({ nonRoutableRequestId })).then(({ detail, error }) => {
      if (!current) return;
      setState({
        loading: false,
        detail: error ? undefined : detail,
        described: error ? describeNonRoutableError(error) : undefined,
      });
    });

    return () => {
      current = false;
    };
  }, [dispatch, nonRoutableRequestId, open]);

  return state;
};

export default useNonRoutableRelayFaxDetail;
